import * as THREE from 'three';
import { lookDotTo, reachDistance, type Xyz } from '../interact/Interact';
import type { SpearAttackCtx, SpearTarget } from './Spear';

export const AXE_RECOVER_S = 0.65;
export const AXE_DAMAGE = 16;
export const AXE_METAL_DAMAGE = 20;
export const AXE_REACH = 1.7;
export const AXE_STAMINA = 8;
export const AXE_LOOK_MIN = 0.55;

const _look = new THREE.Vector3();

export function isAxe(id: string | null | undefined): boolean {
  return id === 'axe' || id === 'metal_axe';
}

export function axeDamage(id: string | null | undefined): number {
  return id === 'metal_axe' ? AXE_METAL_DAMAGE : AXE_DAMAGE;
}

export class Axe {
  recover = 0;
  attacked = 0;

  tick(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) {
      return;
    }
    this.recover = Math.max(0, this.recover - dt);
  }

  drive(dt: number, attack: boolean, ctx: SpearAttackCtx): boolean {
    this.tick(dt);
    return attack ? this.tryAttack(ctx) : false;
  }

  tryAttack(ctx: SpearAttackCtx): boolean {
    if (!isAxe(ctx.held) || this.recover > 0) {
      return false;
    }
    if (!spendIfPresent(ctx.stamina, AXE_STAMINA)) {
      return false;
    }
    this.recover = AXE_RECOVER_S;
    this.attacked += 1;
    ctx.visual?.punch?.();
    firstAxeInCone(ctx.dinos, ctx.camera, ctx.player, ctx.yaw)?.takeHit(axeDamage(ctx.held));
    return true;
  }
}

export function firstAxeInCone(
  dinos: readonly SpearTarget[],
  camera: THREE.Camera,
  player: Xyz,
  yaw?: number,
): SpearTarget | null {
  lookDir(camera, yaw, _look);
  let best: SpearTarget | null = null;
  let bestDist = Infinity;
  for (const dino of dinos) {
    if (!dino.live) {
      continue;
    }
    const dist = axeReach(player, dino);
    if (dist > AXE_REACH) {
      continue;
    }
    if (lookDotTo(camera.position, _look, dino.position) < AXE_LOOK_MIN) {
      continue;
    }
    if (dist < bestDist) {
      best = dino;
      bestDist = dist;
    }
  }
  return best;
}

function lookDir(camera: THREE.Camera, yaw: number | undefined, out: THREE.Vector3): THREE.Vector3 {
  camera.getWorldDirection(out);
  if (out.lengthSq() > 1e-8) {
    return out;
  }
  const facing = yaw ?? 0;
  return out.set(Math.sin(facing), 0, Math.cos(facing));
}

function axeReach(player: Xyz, dino: SpearTarget): number {
  if (typeof dino.interactTarget === 'function') {
    const target = dino.interactTarget();
    return reachDistance(player, {
      id: 'dino',
      prompt: '',
      position: target.position,
      radius: target.radius,
      live: true,
    });
  }
  return Math.hypot(dino.x - player.x, dino.z - player.z);
}

function spendIfPresent(
  pool: SpearAttackCtx['stamina'],
  amount: number,
): boolean {
  if (!pool) {
    return true;
  }
  if (typeof pool.spendStamina === 'function') {
    return pool.spendStamina(amount);
  }
  return true;
}
